<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useMetricsSnapshotStore } from '@/store/metricsSnapshotStore'
import StatusCard from './StatusCard.vue'
import AppMenuBarWithDateRange from '@/components/Menu/AppMenuBarWithDateRange.vue'
import { RefreshCw } from 'lucide-vue-next'
import { searchColorMap, toTitleCase } from '@/utils'
import type { MetricsSnapshot } from '@/models/MetricsSnapshot'
import { type LifecycleState, LIFECYCLE_STATE_OPTIONS, filterByLifecycleState } from '@/models/MetricsSnapshot'

const props = withDefaults(defineProps<{
  lifecycleState?: LifecycleState
}>(), {
  lifecycleState: 'all',
})

const emit = defineEmits<{
  'navigate-to-tenant': [payload: { tenantId: string; tenantName: string }]
  'update:lifecycleState': [state: LifecycleState]
}>()

const metricsStore = useMetricsSnapshotStore()

const searchText = ref('')
const selectedLifecycleState = ref<LifecycleState>(props.lifecycleState)

// Sync with prop changes from parent
watch(() => props.lifecycleState, (val) => {
  selectedLifecycleState.value = val
})

function handleLifecycleStateChange(value: LifecycleState) {
  selectedLifecycleState.value = value
  emit('update:lifecycleState', value)
}

// --- Data fetching ---

async function fetchData() {
  await metricsStore.fetchCrossTenantMetrics({
    tenantOnly: true,
  })
}

function handleSearch(event: Event) {
  const target = event.target as HTMLInputElement
  searchText.value = target?.value ?? ''
}

// --- Computed ---

const loading = computed(() => metricsStore.loading.crossTenant)

const snapshots = computed<MetricsSnapshot[]>(() => {
  const raw = metricsStore.crossTenantData?.snapshots ?? []
  // Only use "all-time" snapshots (date range > 30 days) — filter out stale daily snapshots
  const allTime = raw.filter((s) => {
    if (!s.dateRangeStart || !s.dateRangeEnd) return true
    const start = new Date(s.dateRangeStart).getTime()
    const end = new Date(s.dateRangeEnd).getTime()
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24)
    return daysDiff > 30
  })
  return filterByLifecycleState(allTime, selectedLifecycleState.value)
})

const aggregatedCounts = computed(() => {
  return metricsStore.aggregateCounts(snapshots.value)
})

/** Find the most recent aggregatedUtc across all snapshots */
const lastAggregatedUtc = computed<string | null>(() => {
  let latest: string | null = null
  for (const snap of snapshots.value) {
    if (snap.aggregatedUtc && (!latest || snap.aggregatedUtc > latest)) {
      latest = snap.aggregatedUtc
    }
  }
  return latest
})

/** Check if snapshot data is stale (older than 24 hours) */
const isSnapshotStale = computed(() => {
  if (!lastAggregatedUtc.value) return false
  const lastDate = new Date(lastAggregatedUtc.value)
  const now = new Date()
  const hoursDiff = (now.getTime() - lastDate.getTime()) / (1000 * 60 * 60)
  return hoursDiff > 24
})

/** Human-readable "last updated" string */
const lastUpdatedLabel = computed(() => {
  if (!lastAggregatedUtc.value) return null
  const d = new Date(lastAggregatedUtc.value)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

const statusCards = computed(() => [
  { label: 'Total', count: aggregatedCounts.value.totalCount, variant: 'info' as const },
  { label: 'Succeeded', count: aggregatedCounts.value.succeededCount, variant: 'success' as const },
  { label: 'Failed', count: aggregatedCounts.value.failedCount, variant: 'danger' as const },
  { label: 'Running', count: aggregatedCounts.value.runningCount, variant: 'info' as const },
  { label: 'Pending', count: aggregatedCounts.value.pendingCount, variant: 'warning' as const },
  { label: 'Cancelled', count: aggregatedCounts.value.cancelledCount, variant: 'neutral' as const },
  { label: 'Throttled', count: aggregatedCounts.value.throttledCount, variant: 'warning' as const },
  { label: 'Rejected', count: aggregatedCounts.value.rejectedCount, variant: 'danger' as const },
])

/** Build per-tenant rows from snapshots */
const tenantRows = computed(() => {
  const tenantMap = new Map<string, {
    tenantId: string
    tenantName: string
    succeeded: number
    failed: number
    running: number
    pending: number
    throttled: number
    rejected: number
    total: number
    subscriptionStatus: string
  }>()

  for (const snap of snapshots.value) {
    const existing = tenantMap.get(snap.tenantId)
    if (existing) {
      existing.succeeded += snap.succeededCount
      existing.failed += snap.failedCount
      existing.running += snap.runningCount
      existing.pending += snap.pendingCount
      existing.throttled += snap.throttledCount
      existing.rejected += snap.rejectedCount
      existing.total += snap.totalCount
    } else {
      tenantMap.set(snap.tenantId, {
        tenantId: snap.tenantId,
        tenantName: snap.tenantName || snap.tenantId,
        succeeded: snap.succeededCount,
        failed: snap.failedCount,
        running: snap.runningCount,
        pending: snap.pendingCount,
        throttled: snap.throttledCount,
        rejected: snap.rejectedCount,
        total: snap.totalCount,
        subscriptionStatus: '',
      })
    }
  }

  return Array.from(tenantMap.values())
})

/** Filter tenant rows by search text (case-insensitive substring match on name or ID) */
const filteredTenantRows = computed(() => {
  const query = searchText.value.toLowerCase().trim()
  if (!query) return tenantRows.value
  return tenantRows.value.filter(
    (row) =>
      row.tenantName.toLowerCase().includes(query) ||
      row.tenantId.toLowerCase().includes(query),
  )
})

// --- Table columns ---

const columns = [
  {
    title: 'Tenant Name',
    dataIndex: 'tenantName',
    key: 'tenantName',
    sorter: (a: any, b: any) => a.tenantName.localeCompare(b.tenantName),
  },
  {
    title: 'Succeeded',
    dataIndex: 'succeeded',
    key: 'succeeded',
    sorter: (a: any, b: any) => a.succeeded - b.succeeded,
  },
  {
    title: 'Failed',
    dataIndex: 'failed',
    key: 'failed',
    sorter: (a: any, b: any) => a.failed - b.failed,
  },
  {
    title: 'Running',
    dataIndex: 'running',
    key: 'running',
    sorter: (a: any, b: any) => a.running - b.running,
  },
  {
    title: 'Pending',
    dataIndex: 'pending',
    key: 'pending',
    sorter: (a: any, b: any) => a.pending - b.pending,
  },
  {
    title: 'Throttled',
    dataIndex: 'throttled',
    key: 'throttled',
    sorter: (a: any, b: any) => a.throttled - b.throttled,
  },
  {
    title: 'Rejected',
    dataIndex: 'rejected',
    key: 'rejected',
    sorter: (a: any, b: any) => a.rejected - b.rejected,
  },
  {
    title: 'Total',
    dataIndex: 'total',
    key: 'total',
    sorter: (a: any, b: any) => a.total - b.total,
  },
  {
    title: 'Subscription Status',
    dataIndex: 'subscriptionStatus',
    key: 'subscriptionStatus',
  },
]

// --- Navigation ---

function handleRowClick(record: any) {
  emit('navigate-to-tenant', {
    tenantId: record.tenantId,
    tenantName: record.tenantName,
  })
}

// --- Menu bar buttons ---

const navButtons = ref([
  {
    icon: RefreshCw,
    onClick: fetchData,
    animate: loading,
    title: '',
    toolTip: 'Refresh',
  },
])

// --- Lifecycle ---

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div>
    <!-- Menu bar with search (no date range for all-time view) -->
    <AppMenuBarWithDateRange
      :showSearch="true"
      :showDateRange="false"
      :onSearch="handleSearch"
      :buttons="navButtons"
    />

    <!-- Lifecycle state filter -->
    <div class="mb-4">
      <a-segmented
        v-model:value="selectedLifecycleState"
        :options="LIFECYCLE_STATE_OPTIONS"
        @change="handleLifecycleStateChange"
      />
    </div>

    <!-- Status Cards -->
    <div class="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
      <StatusCard
        v-for="card in statusCards"
        :key="card.label"
        :label="card.label"
        :count="card.count"
        :variant="card.variant"
        :loading="loading"
      />
    </div>

    <!-- Snapshot freshness indicator -->
    <div v-if="lastUpdatedLabel && !loading" class="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
      <span>Snapshot generated:</span>
      <span :class="isSnapshotStale ? 'text-yellow-500 font-medium' : ''">
        {{ lastUpdatedLabel }}
      </span>
      <span v-if="isSnapshotStale" class="inline-flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-yellow-500 border border-yellow-500/30">
        ⚠ Stale — last update was more than 24 hours ago
      </span>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && snapshots.length === 0" class="py-12 text-center text-muted-foreground">
      No metrics available for the selected period.
    </div>

    <!-- Tenant table -->
    <div v-else class="overflow-x-auto">
      <!-- Skeleton loading for table -->
      <div v-if="loading && snapshots.length === 0" class="space-y-3">
        <Skeleton v-for="i in 5" :key="i" class="h-12 w-full" />
      </div>

      <a-table
        v-else
        :dataSource="filteredTenantRows"
        :columns="columns"
        :pagination="{ showSizeChanger: filteredTenantRows.length >= 20 }"
        :rowKey="(record: any) => record.tenantId"
        :customRow="(record: any) => ({
          onClick: () => handleRowClick(record),
          style: 'cursor: pointer',
        })"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'tenantName'">
            <a-tooltip>
              <template #title>{{ record.tenantId }}</template>
              {{ record.tenantName }}
            </a-tooltip>
          </template>

          <template v-else-if="column.key === 'succeeded'">
            <Badge class="bg-green-500 hover:bg-green-500 text-white">
              {{ record.succeeded.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'failed'">
            <Badge class="bg-red-500 hover:bg-red-500 text-white">
              {{ record.failed.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'running'">
            <Badge class="bg-blue-500 hover:bg-blue-500 text-white">
              {{ record.running.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'pending'">
            <Badge class="bg-yellow-500 hover:bg-yellow-500 text-white">
              {{ record.pending.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'throttled'">
            <Badge class="bg-yellow-500 hover:bg-yellow-500 text-white">
              {{ record.throttled.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'rejected'">
            <Badge class="bg-red-500 hover:bg-red-500 text-white">
              {{ record.rejected.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'total'">
            {{ record.total.toLocaleString() }}
          </template>

          <template v-else-if="column.key === 'subscriptionStatus'">
            <Badge v-if="record.subscriptionStatus" :class="searchColorMap(record.subscriptionStatus)">
              {{ toTitleCase(record.subscriptionStatus) }}
            </Badge>
            <span v-else class="text-muted-foreground">—</span>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>
